/**
 * Dates in, an agenda out. No DOM and no network, so the rules are testable.
 *
 * Ranges are rolling rather than calendar: "this week" on a Thursday means
 * the next seven days, because what is due on Monday matters more than
 * whether Monday is in the same calendar week.
 */

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const SPAN = { today: 1, week: 7, month: 30 };

/** `YYYY-MM-DD` in local time. `toISOString` would be UTC, which is a day out. */
export function isoDay(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function addDays(date, days) {
  // Constructing from parts rather than adding milliseconds, so a daylight
  // saving change does not move the answer by an hour and then a day.
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function rangeFor(view, now) {
  const span = SPAN[view] ?? SPAN.today;
  return { start: isoDay(now), end: isoDay(addDays(now, span - 1)) };
}

/**
 * Only the assignments whose LOCAL day falls inside `[start, end]`.
 *
 * The Lambda's own window is padded a day on each side (its compare is
 * against Canvas's UTC timestamps, not local ones), so its reply can include
 * work from the day before or after what was actually asked for. This is the
 * precise filter that turns that generous reply back into the exact range the
 * caller wanted, entirely in local time.
 */
export function withinRange(assignments, { start, end }) {
  return assignments.filter((item) => {
    if (!item.due) return false;
    const dueDate = new Date(item.due);
    if (isNaN(dueDate.getTime())) return false; // Drop malformed dates like normal ones.
    const day = isoDay(dueDate);
    return start <= day && day <= end;
  });
}

export function groupByDay(assignments, now) {
  const byDay = new Map();

  for (const item of assignments) {
    if (!item.due) continue;
    // Convert to local date. Slicing the UTC string would mislabel UTC evening
    // deadlines that are local morning-of — a 11:59pm local deadline becomes
    // 06:59:59Z the next day, which is bucketed and labelled Tomorrow not Today.
    const dueDate = new Date(item.due);
    if (isNaN(dueDate.getTime())) continue; // Drop malformed dates like normal ones.
    const day = isoDay(dueDate);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push(item);
  }

  const today = isoDay(now);
  const tomorrow = isoDay(addDays(now, 1));

  return [...byDay.keys()].sort().map((iso) => ({
    iso,
    label: labelFor(iso, today, tomorrow),
    items: byDay.get(iso),
  }));
}

/**
 * `Today`, `Tomorrow`, or `Saturday 5 September`, for a local `YYYY-MM-DD`.
 *
 * Exported so the calendar screen names its days the same way this one does.
 * Two spellings of the same date on one page reads as two features.
 */
export function dayLabel(iso, now) {
  return labelFor(iso, isoDay(now), isoDay(addDays(now, 1)));
}

function labelFor(iso, today, tomorrow) {
  if (iso === today) return 'Today';
  if (iso === tomorrow) return 'Tomorrow';
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return `${DAYS[date.getDay()]} ${day} ${MONTHS[month - 1]}`;
}
